import { createSelector } from '@ngxs/store';
import _ from 'lodash';
import { TableHelper } from 'src/app/helpers';
import { SeatedDiner, SupportPageData } from 'src/app/models';
import { AppSelectors } from 'src/app/store/app/app.selectors';
import { DinersSelectors } from 'src/app/store/diners/diners.selectors';
import { HardwareDeviceSelectors } from 'src/app/store/hardware-device/hardware-device.selector';
import { HostModeSelectors } from 'src/app/store/host-mode/host-mode.selector';
import { LocationSelectors } from 'src/app/store/location/location.selectors';
import { TableReservationSelectors } from 'src/app/store/tables/reservations/table-reservation.selectors';
import { TablesStateSelectors } from 'src/app/store/tables/tables.selectors';
import { TicketItemsStateSelectors } from 'src/app/store/ticket-items/ticket-items-state.selectors';
import { TicketsStateSelectors } from 'src/app/store/tickets/tickets.selectors';

export class UtilStateSelector {
  // move code from src/app/store/utils.selectors.ts to here (getTables)
  static getTables = createSelector(
    [
      TablesStateSelectors.getFormattedTables,
      HostModeSelectors.getPendingSeatAssignments,
      TableReservationSelectors.getReservationTables,
      LocationSelectors.reservation_settings
    ],
    (tables, pendingSeatAssignments, reservedTables, reservationSettings) =>
      !tables
        ? tables
        : TableHelper.insertReservedTableStatus(
            _.cloneDeep(tables || []),
            reservedTables,
            reservationSettings
          ).map((modifyingTable) =>
            TableHelper.modifyTable(modifyingTable, pendingSeatAssignments)
          )
  );

  // move code from src/app/store/host-mode/host-mode-state.selector.ts (deleted file) to here (getSeatedDiners)
  static getSeatedDiners = createSelector(
    [UtilStateSelector.getTables],
    (tables) =>
      tables.reduce(
        (prev, curr) => [
          ...prev,
          ...curr.seats
            .filter((seat) => seat.ticket_created_at)
            .map(
              ({
                diner_id,
                last_name,
                first_name,
                floorplan_status,
                ticket_created_at
              }) => ({
                diner_id,
                last_name,
                first_name,
                name:
                  !first_name && !last_name
                    ? 'Guest Diner'
                    : [first_name, last_name].join(' '),
                table_name: curr.name,
                table_id: curr.id,
                floorplan_status,
                created_at: new Date(ticket_created_at)
              })
            )
        ],
        [] as SeatedDiner[]
      )
  );

  // move code from src/app/store/host-mode/host-mode-state.selector.ts (deleted file) to here (getAssignedDiners)
  static getAssignedDiners = createSelector(
    [
      HostModeSelectors.getPendingSeatAssignments,
      UtilStateSelector.getSeatedDiners,
      UtilStateSelector.getTables
    ],
    (pendingSeatAssignments, seatedDiners, tables) =>
      [...seatedDiners, ...pendingSeatAssignments]
        .filter((assignment) =>
          tables.some((table) => table.id === assignment.table_id)
        )
        .sort((a, b) => {
          const aTableNumber = Number(
            a.table_name
              .trim()
              .toLocaleLowerCase()
              .substring(a.table_name.lastIndexOf('table') + 6)
              .trim()
          );
          const bTableNumber = Number(
            b.table_name
              .trim()
              .toLocaleLowerCase()
              .substring(b.table_name.lastIndexOf('table') + 6)
              .trim()
          );

          if (aTableNumber === bTableNumber) {
            if ('floorplan_status' in a && 'floorplan_status' in b) {
              return 0;
            } else if ('floorplan_status' in a) {
              return -1;
            } else {
              return 1;
            }
          }

          return aTableNumber - bTableNumber;
        })
  );

  // move code from  utils.selectors.ts to here (getSelectedTicketMealId)
  static getSelectedTicketMealId = createSelector(
    [TicketsStateSelectors.ticketsById, TicketsStateSelectors.selectedTicketId],
    (ticketsById, selectedTicketId) => {
      const isInvalid =
        selectedTicketId === null ||
        !ticketsById ||
        !_.has(ticketsById, selectedTicketId);

      return isInvalid ? null : ticketsById[selectedTicketId].meal_id;
    }
  );

  // move code from utils.selectors.ts to here (getSelectedDiscountId)
  static getSelectedDiscountId = createSelector(
    [
      TicketsStateSelectors.ticketsById,
      TicketsStateSelectors.selectedTicketId,
      TicketItemsStateSelectors.getSelectedTicketItem
    ],
    (ticketsById, selectedTicketId, ticketItem) => {
      const ticket =
        !selectedTicketId || !ticketsById
          ? null
          : ticketsById[selectedTicketId];

      return !!ticketItem
        ? ticketItem.applied_discount_id
        : ticket.applied_discount_id;
    }
  );

  //move from app.selectors.ts
  static getSupportData = createSelector(
    [
      AppSelectors.facility_details,
      AppSelectors.device,
      HardwareDeviceSelectors.version,
      HardwareDeviceSelectors.localOsAndUuid,
      HardwareDeviceSelectors.webViewVersion
    ],
    (facility_details, device, version, { local_os, uuid }, webviewVersion) =>
      <SupportPageData>(<SupportPageData>{
        webviewVersion,
        version,
        uuid,
        token: device.key,
        device_name: device.name,
        tenant: device.tenant,
        os: local_os?.name,
        facility: facility_details?.name || ''
      })
  );

  //move from app.selectors.ts
  static getPosManagerOperator = createSelector(
    [
      AppSelectors.tempOperator,
      LocationSelectors.isSelfServeKioskLocation,
      AppSelectors.getOperator
    ],
    (tempOperator, isSelfService, operator) =>
      isSelfService ? tempOperator : operator
  );

  // move from diners.selectors.ts
  static getSelectedDiner = createSelector(
    [DinersSelectors.dinersById, TicketsStateSelectors.getSelectedDinerId],
    (dinerById, selectedDinerId) =>
      selectedDinerId && dinerById && dinerById[selectedDinerId]
  );
}
